/**
 * useSpeechSynthesis — thin hook over the browser's SpeechSynthesis API.
 * Zero server cost, ships with Chrome / Safari / Edge, supports every
 * Sancta language (auto-picks the best matching voice on the platform).
 *
 * Only ONE utterance can be active at a time across all consumers of the
 * hook — starting a new `speak()` cancels any previous one. The
 * currently-speaking message is tracked via `currentId` so a chat widget
 * can tag exactly which reply's speaker button is active.
 */
import { useEffect, useRef, useState } from "react";

/**
 * BCP-47 code map for the 12 Sancta languages. Uses regional codes that
 * major TTS engines actually ship voices for.
 */
const LANG_MAP: Record<string, string> = {
  en: "en-US",
  hi: "hi-IN",
  ta: "ta-IN",
  te: "te-IN",
  kn: "kn-IN",
  ml: "ml-IN",
  bn: "bn-IN",
  mr: "mr-IN",
  gu: "gu-IN",
  es: "es-ES",
  ar: "ar-SA",
  fr: "fr-FR",
};

interface SpeechState {
  supported: boolean;
  speaking: boolean;
  currentId: string | null;
  speak: (id: string, text: string, lang?: string) => void;
  stop: () => void;
}

export function useSpeechSynthesis(): SpeechState {
  const supported =
    typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
  const [speaking, setSpeaking] = useState(false);
  const [currentId, setCurrentId] = useState<string | null>(null);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  function pickVoice(bcpLang: string): SpeechSynthesisVoice | undefined {
    if (!supported) return undefined;
    const voices = window.speechSynthesis.getVoices();
    if (!voices || voices.length === 0) return undefined;
    // Prefer an exact BCP-47 match, then fall back to the language prefix.
    const exact = voices.find(v => v.lang.toLowerCase() === bcpLang.toLowerCase());
    if (exact) return exact;
    const prefix = bcpLang.split("-")[0].toLowerCase();
    return voices.find(v => v.lang.toLowerCase().startsWith(prefix));
  }

  function speak(id: string, text: string, lang: string = "en") {
    if (!supported || !text?.trim()) return;
    try {
      window.speechSynthesis.cancel();  // hard-stop any prior utterance
    } catch {}
    const utter = new SpeechSynthesisUtterance(text);
    const bcp = LANG_MAP[lang] || lang;
    utter.lang = bcp;
    const voice = pickVoice(bcp);
    if (voice) utter.voice = voice;
    utter.rate = 1;
    utter.pitch = 1;
    utter.onend = () => {
      setSpeaking(false);
      setCurrentId(null);
    };
    utter.onerror = () => {
      setSpeaking(false);
      setCurrentId(null);
    };
    utteranceRef.current = utter;
    setCurrentId(id);
    setSpeaking(true);
    try {
      window.speechSynthesis.speak(utter);
    } catch {
      setSpeaking(false);
      setCurrentId(null);
    }
  }

  function stop() {
    if (!supported) return;
    try {
      window.speechSynthesis.cancel();
    } catch {}
    setSpeaking(false);
    setCurrentId(null);
  }

  // Cleanup on unmount so a widget closing doesn't leave a rogue voice speaking.
  useEffect(() => {
    return () => {
      if (supported) {
        try { window.speechSynthesis.cancel(); } catch {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Warm-up voice list on first mount — some browsers only populate the
  // voices array after the `voiceschanged` event fires.
  useEffect(() => {
    if (!supported) return;
    const bump = () => window.speechSynthesis.getVoices();
    bump();
    window.speechSynthesis.addEventListener?.("voiceschanged", bump);
    return () => window.speechSynthesis.removeEventListener?.("voiceschanged", bump);
  }, [supported]);

  return { supported, speaking, currentId, speak, stop };
}

export default useSpeechSynthesis;
